/**
 * OdinoRail — the docked right-rail companion (ADR-0024 S2.2).
 *
 * Wraps the reusable `OdinoChat` in a collapsible right-side panel so you can
 * build WITH Odino while looking at your app. **Controlled**: the host
 * (`AppShell`) owns the open state, because it must shrink the page content by
 * the rail's width when open (so the rail sits BESIDE the content, not over it).
 * The LAUNCHER is the "Odino" button in the Topbar header — NOT a floating button
 * (which could cover page content). Closed → this component renders NOTHING; open
 * → the panel + `OdinoChat` (its session/SSE spin up only then). The rail itself
 * is `fixed`, so the host reserves space with a matching right margin.
 */
import { Bot, X } from "lucide-react";
import * as React from "react";

import { useFeature } from "@/lib/entitlements";
import { OdinoChat } from "./OdinoChat";

/** Rail width — kept here so `AppShell` reserves the exact matching margin. */
export const ODINO_RAIL_WIDTH = 440;

export function OdinoRail({
  open,
  onOpenChange,
  onBusyChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Bubbles Odino's in-flight state up to `AppShell` so it can lock (make
   *  `inert`) the builder while a live build runs (ADR-0024 slice D). */
  onBusyChange?: (busy: boolean) => void;
}) {
  // Odino is part of the AI plane — render nothing when the plan excludes it, and
  // reset a stale persisted-open so `AppShell` collapses the reserved right margin.
  const aiPlane = useFeature("ai_plane");
  React.useEffect(() => {
    if (!aiPlane && open) onOpenChange(false);
  }, [aiPlane, open, onOpenChange]);
  if (!aiPlane) return null;

  // Closed → render nothing. The launcher is the "Odino" button in the Topbar
  // header (no floating button — it could cover page content).
  if (!open) return null;

  return (
    <aside
      className="fixed right-0 top-0 z-40 flex h-full w-full flex-col border-l border-border bg-background shadow-2xl md:w-[440px]"
      style={{ maxWidth: "100vw" }}
    >
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Bot className="h-4 w-4 text-accent" strokeWidth={1.5} />
        <span className="text-sm font-semibold">Odino</span>
        <span className="text-xs text-muted-foreground">— compagno di build</span>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="ml-auto rounded-md p-1 text-muted-foreground hover:bg-elevated hover:text-foreground"
          aria-label="Chiudi"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1">
        <OdinoChat embedded onBusyChange={onBusyChange} />
      </div>
    </aside>
  );
}
